import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von06GiNutrition } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von06GiNutritionFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von06GiNutritionForm: React.FC<Von06GiNutritionFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  const [nutritionPrimaHranireZi, setNutritionPrimaHranireZi] = useState<string>('');
  const [nec, setNec] = useState(false);
  const [sip, setSip] = useState<string>('');
  const [necWhere, setNecWhere] = useState<string>('');
  const [probioticsAny, setProbioticsAny] = useState(false);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getGiNutrition(patientId);

      if (data) {
        setHasExistingData(true);
        setNutritionPrimaHranireZi(data.nutrition_prima_hranire_zi || '');
        setNec(Boolean(data.nec));
        setSip(data.sip?.toString() || '');
        setNecWhere(data.nec_where?.toString() || '');
        setProbioticsAny(Boolean(data.probiotics_any));
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von06GiNutrition> = {
        patient_id: patientId,
        nutrition_prima_hranire_zi: nutritionPrimaHranireZi || undefined,
        nec: nec,
        sip: sip ? parseInt(sip) : undefined,
        nec_where: necWhere ? parseInt(necWhere) : undefined,
        probiotics_any: probioticsAny
      };

      if (hasExistingData) {
        await vonDataService.updateGiNutrition(patientId, formData);
      } else {
        await vonDataService.createGiNutrition(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 06: Digestiv și nutriție</CardTitle>
        <CardDescription>Date despre nutriție enterală și patologie digestivă</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First feeding */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Prima nutriție enterală</h3>
            <div className="space-y-2">
              <Label htmlFor="firstFeeding">Ziua primei nutriții enterale</Label>
              <Input
                id="firstFeeding"
                value={nutritionPrimaHranireZi}
                onChange={(e) => setNutritionPrimaHranireZi(e.target.value)}
                placeholder="Ex: ziua 1, ziua 2"
                disabled={readOnly}
              />
            </div>
          </div>

          {/* NEC */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Enterocolită necrozantă (NEC)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="nec"
                  checked={nec}
                  onCheckedChange={(checked) => setNec(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="nec" className="text-sm font-normal cursor-pointer">
                  Enterocolită necrozantă
                </Label>
              </div>

              {nec && (
                <div className="space-y-2">
                  <Label htmlFor="necWhere">Unde a apărut NEC</Label>
                  <Select value={necWhere} onValueChange={setNecWhere} disabled={readOnly}>
                    <SelectTrigger id="necWhere">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">NICU local</SelectItem>
                      <SelectItem value="2">NICU de transfer</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="sip">Perforație intestinală focală</Label>
                <Select value={sip} onValueChange={setSip} disabled={readOnly}>
                  <SelectTrigger id="sip">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Confirmat chirurgical</SelectItem>
                    <SelectItem value="2">Diagnosticat clinic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Probiotics */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Probiotice</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="probioticsAny"
                checked={probioticsAny}
                onCheckedChange={(checked) => setProbioticsAny(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="probioticsAny" className="text-sm font-normal cursor-pointer">
                Probiotice administrate
              </Label>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
